import EditOutlined from '@mui/icons-material/EditOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitKeys } from 'api/queries/UnitQueries';
import { AreaName } from 'domain/constants';
import { DialogIconButton } from 'library/components/controls/buttons/DialogIconButton';
import { OperationType } from 'library/models/enums/OperationType';
import { IsExternal } from 'library/types';
import { createArea } from 'library/utils/helpers/AreaHelper';
import Authorize from 'main/auth/Authorize';

import { CustomerBoxProps, EditCustomerProps } from '../types';
import DisconnectUnitManagerDialog from './DisconnectUnitManagerDialog';

interface ConnectedCustomerCardDisplayButtonsProps
	extends CustomerBoxProps,
		EditCustomerProps,
		IsExternal {}

export const ConnectedCustomerCardDisplayButtons = ({
	customer,
	isExternal,
	isOwner,
	onEditCustomer,
	unitId,
}: ConnectedCustomerCardDisplayButtonsProps) => {
	const { t } = useTranslation(['customer', 'common']);
	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();

	const handleOnEditClick = useCallback(() => {
		if (customer) {
			onEditCustomer(customer);
		}
	}, [customer, onEditCustomer]);

	const handleOnDisconnectClick = useCallback(async () => {
		if (customer) {
			await api
				.disconnectUnitManagerFromUnit({
					unitManagerId: customer.id,
					unitId,
				})
				.then(async () => {
					await queryClient.invalidateQueries({
						queryKey: unitKeys.connectedCustomers(unitId),
					});
					enqueueSnackbar(
						t('customer:disconnect-um-success-snackbar'),
						{ variant: 'success' }
					);
				})
				.catch(() => {
					enqueueSnackbar(t('common:error-unexpected'), {
						variant: 'error',
					});
				});
		}
	}, [api, customer, enqueueSnackbar, queryClient, t, unitId]);

	return (
		<Authorize
			area={createArea(AreaName.Consumer, OperationType.Write)}
			displayIntermediatePages={false}
			redirectWhenNotAuthorized={false}>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					display: 'flex',
				}}>
				{!isExternal && !isOwner && (
					<DialogIconButton
						disabled={!customer}
						Icon={<LinkOffIcon />}
						DialogComponent={DisconnectUnitManagerDialog}
						dialogProps={{
							children: t(
								'customer:disconnect-um-dialog-content'
							),
							title: t('customer:disconnect-um-dialog-title'),
							onConfirm: handleOnDisconnectClick,
						}}
						tooltipText={t('customer:disconnect-um-tooltip')}
					/>
				)}
				<Tooltip
					title={t(
						isOwner
							? 'customer:edit-owner-tooltip'
							: 'customer:edit-um-tooltip'
					)}>
					<span>
						<IconButton
							disabled={
								customer == null ||
								customer.isAnonymized === true
							}
							onClick={handleOnEditClick}>
							<EditOutlined />
						</IconButton>
					</span>
				</Tooltip>
			</Box>
		</Authorize>
	);
};
